import { useToast } from "@chakra-ui/react";
import { StripeBillingPortalAction } from "@syla/shared/types/requests/CreateStripeBillingPortalRequest";
import { SubscriptionWithPlansAndAccess } from "@syla/shared/types/responses/GetSubscriptionsResponse";
import { useState } from "react";
import { useLocation } from "react-router-dom";
import { errorToastOptions } from "../../../components/molecules/errorToastOptions";
import { captureRequestError } from "../../../helper/captureRequestError";
import { getFullPageUrl } from "../../../helper/getFullPageUrl";
import { redirectTo } from "../../../helper/RedirectTo";
import { useMutationCreateStripeBillingPortalSession } from "../../../store/useMutationCreateStripeBillingPortalSession";

export const useSubscriptionActions = ({
  currentSub,
  accountId,
  setIsRedirecting,
}: {
  currentSub: SubscriptionWithPlansAndAccess<string> | undefined;
  accountId: string;
  setIsRedirecting: (v: boolean) => void;
}) => {
  const location = useLocation();
  const toast = useToast(errorToastOptions);
  const { mutateAsync: createStripeBillingPortalSession } =
    useMutationCreateStripeBillingPortalSession();

  const [activeAction, setActiveAction] =
    useState<StripeBillingPortalAction | null>(null);

  const openMainPortal = async () => {
    try {
      setIsRedirecting(true);
      setActiveAction(StripeBillingPortalAction.MainPortal);

      const response = await createStripeBillingPortalSession({
        accountId,
        action: StripeBillingPortalAction.MainPortal,
        returnUrl: getFullPageUrl(location),
      });

      redirectTo(response.portalUrl);
    } catch (error) {
      setIsRedirecting(false);
      captureRequestError(error);
      toast({
        title: "Failed to open billing portal",
        description: "An unexpected error occurred. Please try again later.",
      });
    } finally {
      setActiveAction(null);
    }
  };

  const updateSubscription = async () => {
    try {
      if (!currentSub?.stripeSubscriptionId) {
        throw new Error("Missing subscription ID");
      }

      setIsRedirecting(true);
      setActiveAction(StripeBillingPortalAction.UpdateSubscription);

      const response = await createStripeBillingPortalSession({
        accountId,
        action: StripeBillingPortalAction.UpdateSubscription,
        stripeSubscriptionId: currentSub.stripeSubscriptionId,
        returnUrl: getFullPageUrl(location),
      });

      redirectTo(response.portalUrl);
    } catch (error) {
      setIsRedirecting(false);
      captureRequestError(error);
      toast({
        title: "Failed to open billing portal",
        description: "An unexpected error occurred. Please try again later.",
      });
    } finally {
      setActiveAction(null);
    }
  };

  const cancelSubscription = async () => {
    try {
      if (!currentSub?.stripeSubscriptionId) {
        throw new Error("Missing subscription ID");
      }

      setIsRedirecting(true);
      setActiveAction(StripeBillingPortalAction.Cancel);

      const response = await createStripeBillingPortalSession({
        accountId,
        action: StripeBillingPortalAction.Cancel,
        stripeSubscriptionId: currentSub.stripeSubscriptionId,
        returnUrl: getFullPageUrl(location),
      });

      redirectTo(response.portalUrl);
    } catch (error) {
      setIsRedirecting(false);
      captureRequestError(error);
      toast({
        title: "Failed to open billing portal",
        description: "An unexpected error occurred. Please try again later.",
      });
    } finally {
      setActiveAction(null);
    }
  };

  return {
    activeAction,
    openMainPortal,
    updateSubscription,
    cancelSubscription,
  };
};
